import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { Plus, FileText, ChevronRight, Trash2, Users, Target, ClipboardCheck, TrendingUp, Sparkles, Sun, Moon, Radar as RadarIcon, Lock } from "lucide-react";
import CommandCenter from "@/components/dashboard/CommandCenter";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { tr } from "@/lib/i18n";
import WhatNextPaywall from "@/components/WhatNextPaywall";
import {
  RADAR_TOTAL_COUNT,
  RADAR_LAST_UPDATE_SHORT,
  RADAR_NEXT_UPDATE_SHORT,
  radarUnlocked,
} from "@/constants/radarData";

export default function Dashboard() {
  const { user, access, lang } = useAuth();
  const t = (k) => tr(k, lang);
  const [scenes, setScenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightMode, setLightMode] = useState(false);
  const [scenePaywallOpen, setScenePaywallOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/scenes");
        setScenes(r.data);
      } finally { setLoading(false); }
    })();
  }, []);

  const del = async (id) => {
    if (!window.confirm(t("dash.confirm_delete"))) return;
    try {
      await api.delete(`/scenes/${id}`);
      setScenes(prev => prev.filter(s => s.scene_id !== id));
      toast.success(t("dash.deleted"));
    } catch { toast.error(t("dash.delete_failed")); }
  };

  const goNew = () => {
    if (access && !( (["monthly","annual","sprint","start","light"].includes(access.plan) && (access.usage?.scenes_used_period ?? 0) < (access.limits?.scenes ?? 0)) ||
        access.scene_credits > 0 ||
        access.scenes_used_free < access.free_limit)) {
      setScenePaywallOpen(true);
    } else {
      navigate("/scenes/new");
    }
  };

  return (
    <div className={lightMode ? "theme-light" : ""} data-testid="dashboard-root">
    <div className="relative">
      <div className="relative flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="mono text-[11px] tracking-[0.4em] text-amber-500 uppercase">{t("dash.rehearsal_room")}</div>
          <h1 className="serif text-5xl text-stone-100 mt-2">{t("dash.welcome")}{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.</h1>
          <p className="text-stone-400 mt-3 max-w-2xl leading-relaxed">{t("dash.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          {access?.limits?.scenes > 0 && (
            <div
              data-testid="dash-scenes-counter"
              className="mono text-[11px] tracking-widest text-stone-400 uppercase mr-1"
              title={lang === "pl" ? "Sceny w tym okresie planu" : "Scenes used this period"}
            >
              {lang === "pl" ? "Sceny" : lang === "ja" ? "シーン" : "Scenes"}: {(access?.usage?.scenes_used_period ?? 0)} / {access.limits.scenes}
            </div>
          )}
          {access?.limits?.scenes > 0 && (
            <div
              data-testid="dash-variants-counter"
              className="mono text-[11px] tracking-widest text-stone-400 uppercase mr-1"
              title={lang === "pl" ? "Warianty w tym okresie planu" : "Variants used this period"}
            >
              {lang === "pl" ? "Warianty" : lang === "ja" ? "バリエーション" : "Variants"}: {(access?.usage?.variants_used_period ?? 0)} / {access.limits.variants ?? ((access.limits.variants_per_scene ?? 0) * access.limits.scenes)}
            </div>
          )}
          <button
            data-testid="theme-toggle-btn"
            onClick={() => setLightMode(v => !v)}
            className="ghost-btn !py-2 !px-3 text-xs"
            title={lightMode ? "Dark mode" : "Light mode (prototype)"}
          >
            {lightMode ? <Moon size={14} /> : <Sun size={14} />}
            {lightMode ? "Dark" : "Light · prototyp"}
          </button>
          <button data-testid="new-scene-btn" data-onboarding="new-scene" onClick={goNew} className="pill-btn">
            <Plus size={16}/> {t("dash.new_scene")}
          </button>
        </div>
      </div>

      {((access?.plan === "free" && access?.scenes_used_free < access?.free_limit) ||
        (access?.plan === "start" && (access?.usage?.scenes_used_period ?? 0) < (access?.limits?.scenes ?? 0))) && (
        <motion.div initial={{opacity:0, y:8}} animate={{opacity:1, y:0}} className="mt-8 paper p-5 border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-stone-900 to-stone-900">
          <div className="flex items-center gap-3">
            <span className="lm-icon-chip lm-icon-chip--warm" aria-hidden><Sparkles size={18}/></span>
            <div className="mono text-[11px] tracking-[0.3em] text-amber-500 uppercase">{t("dash.free_first_title")}</div>
          </div>
          <div className="serif text-2xl text-stone-100 mt-2">{t("dash.free_first_body")}</div>
        </motion.div>
      )}

      {/* Main sections — unified card style, differentiated only by icon accent */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Tile 0 — LEO RADAR (PL only, full width, compact horizontal, always first) */}
        {lang === "pl" && (() => {
          const unlocked = radarUnlocked(access);
          return (
            <motion.div
              data-testid="dash-tile-radar"
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className="paper p-5 md:col-span-2 relative overflow-hidden"
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <span className="lm-icon-chip lm-icon-chip--warm shrink-0" aria-hidden><RadarIcon size={18}/></span>
                  <div className="flex-1 min-w-0">
                    <div className="mono text-[11px] tracking-[0.3em] text-amber-500 uppercase">
                      LEO RADAR · POLSKA
                    </div>
                    <div className="serif text-2xl text-stone-100 mt-1">Produkcje na radarze</div>
                    <p className="text-stone-400 text-sm mt-1 max-w-2xl">
                      Filmy i seriale, o których warto wiedzieć wcześniej.
                    </p>
                    <div
                      className="mono text-[11px] tracking-widest text-stone-400 mt-2"
                      data-testid="dash-radar-count"
                    >
                      {RADAR_TOTAL_COUNT} projektów · aktualizacja {RADAR_LAST_UPDATE_SHORT}
                    </div>
                    <div className="mono text-[10px] tracking-widest text-stone-500 mt-1">
                      Kolejna aktualizacja: {RADAR_NEXT_UPDATE_SHORT}
                    </div>
                  </div>
                </div>
                <div className="md:pl-4 shrink-0">
                  {unlocked ? (
                    <button
                      data-testid="dash-radar-open-btn"
                      onClick={() => navigate("/radar")}
                      className="pill-btn w-full md:w-auto"
                    >
                      OTWÓRZ RADAR →
                    </button>
                  ) : (
                    <button
                      data-testid="dash-radar-unlock-btn"
                      onClick={() => navigate("/radar")}
                      className="pill-btn w-full md:w-auto"
                    >
                      <Lock size={12}/> ODBLOKUJ RADAR →
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })()}

        {/* Tile 1 — Upload a Scene (sage) */}
        <motion.button
          data-testid="dash-tile-new-scene"
          onClick={goNew}
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="paper p-6 text-left group hover:border-amber-500/50 transition-colors relative overflow-hidden"
        >
          <div className="flex items-start gap-4">
            <span className="lm-icon-chip" aria-hidden><FileText size={20}/></span>
            <div className="flex-1 min-w-0">
              <div className="serif text-3xl text-stone-100 mt-1">{t("dash.tile.upload.title")}</div>
              <p className="text-stone-400 text-sm mt-2 max-w-md">{t("dash.tile.upload.subtitle")}</p>
              <div className="mt-5 flex flex-wrap items-center gap-4">
                <span className="inline-flex items-center gap-2 mono text-[11px] uppercase tracking-widest text-amber-500 group-hover:text-amber-400">
                  <Plus size={12}/> {t("dash.tile.upload.cta")}
                </span>
                <span
                  data-testid="dash-tile-my-scenes"
                  onClick={(e) => {
                    e.stopPropagation();
                    document.getElementById("dashboard-my-scenes")?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  className="inline-flex items-center gap-2 mono text-[11px] uppercase tracking-widest text-amber-500 group-hover:text-amber-400 hover:text-amber-400 cursor-pointer"
                >
                  ↓ {t("dash.tile.upload.my_scenes")}
                </span>
              </div>
            </div>
            <span className="lm-icon-decor hidden md:inline-flex" aria-hidden><FileText size={30} strokeWidth={1.5}/></span>
          </div>
        </motion.button>

        {/* Tile 2 — Workflow & CRM (sage) */}
        <motion.button
          data-testid="dash-tile-crm"
          onClick={() => navigate("/crm")}
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="paper p-6 text-left group hover:border-amber-500/50 transition-colors relative overflow-hidden"
        >
          <div className="flex items-start gap-4">
            <span className="lm-icon-chip" aria-hidden><Users size={20}/></span>
            <div className="flex-1 min-w-0">
              <div className="mono text-[11px] tracking-[0.3em] text-amber-500 uppercase">
                {t("crm.dash_tile_eyebrow")}
              </div>
              <div className="serif text-3xl text-stone-100 mt-2">{t("crm.dash_tile_title")}</div>
              <p className="text-stone-400 text-sm mt-2 max-w-md">{t("crm.dash_tile_body")}</p>
              <div className="mt-5 inline-flex items-center gap-2 mono text-[11px] uppercase tracking-widest text-amber-500 group-hover:text-amber-400">
                <ChevronRight size={12}/> {t("crm.open")}
              </div>
            </div>
            <span className="lm-icon-decor hidden md:inline-flex" aria-hidden><Users size={30} strokeWidth={1.5}/></span>
          </div>
        </motion.button>

        {/* Tile 3 — Career Path (sage — long-term / growth) */}
        <motion.button
          data-testid="dash-tile-career"
          onClick={() => navigate("/career")}
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="paper p-6 text-left group hover:border-amber-500/50 transition-colors relative overflow-hidden"
        >
          <div className="flex items-start gap-4">
            <span className="lm-icon-chip" aria-hidden><Target size={20}/></span>
            <div className="flex-1 min-w-0">
              <div className="mono text-[11px] tracking-[0.3em] text-amber-500 uppercase">
                {t("path.dash_tile_eyebrow")}
              </div>
              <div className="serif text-3xl text-stone-100 mt-2">{t("path.dash_tile_title")}</div>
              <p className="text-stone-400 text-sm mt-2 max-w-md">{t("path.dash_tile_body")}</p>
              <div className="mt-5 inline-flex items-center gap-2 mono text-[11px] uppercase tracking-widest text-amber-500 group-hover:text-amber-400">
                <ChevronRight size={12}/> {t("path.open")}
              </div>
            </div>
            <span className="lm-icon-decor hidden md:inline-flex" aria-hidden><TrendingUp size={30} strokeWidth={1.5}/></span>
          </div>
        </motion.button>

        {/* Tile 4 — Operational Tasks (warm orange — urgent / today) */}
        <motion.button
          data-testid="dash-tile-ops"
          onClick={() => navigate("/ops/tasks")}
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="paper p-6 text-left group hover:border-amber-500/50 transition-colors relative overflow-hidden"
        >
          <div className="flex items-start gap-4">
            <span className="lm-icon-chip lm-icon-chip--warm" aria-hidden><ClipboardCheck size={20}/></span>
            <div className="flex-1 min-w-0">
              <div className="mono text-[11px] tracking-[0.3em] text-amber-500 uppercase">
                {t("tasks.dash_tile_eyebrow")}
              </div>
              <div className="serif text-3xl text-stone-100 mt-2">{t("tasks.dash_tile_title")}</div>
              <p className="text-stone-400 text-sm mt-2 max-w-md">{t("tasks.dash_tile_body")}</p>
              <div className="mt-5 inline-flex items-center gap-2 mono text-[11px] uppercase tracking-widest text-amber-500 group-hover:text-amber-400">
                <ChevronRight size={12}/> {t("tasks.open")}
              </div>
            </div>
            <span className="lm-icon-decor lm-icon-decor--warm hidden md:inline-flex" aria-hidden><ClipboardCheck size={30} strokeWidth={1.5}/></span>
          </div>
        </motion.button>
      </div>

      <div className="mt-10">
        <CommandCenter />
      </div>

      <div id="dashboard-my-scenes" className="mt-10">
        <div className="label-cap">{t("dash.your_scenes")}</div>
        {loading ? (
          <div className="mono text-stone-500 mt-4 text-sm">{t("dash.loading")}</div>
        ) : scenes.length === 0 ? (
          <div className="paper p-10 mt-4 text-center">
            <FileText className="mx-auto text-stone-500" />
            <div className="serif text-2xl text-stone-200 mt-3">{t("dash.empty_title")}</div>
            <div className="text-stone-500 mt-1 text-sm">{t("dash.empty_body")}</div>
            <button data-testid="empty-new-scene" onClick={goNew} className="pill-btn mt-6">{t("dash.upload_first")}</button>
          </div>
        ) : (
          <ul className="mt-4 divide-y divide-stone-900 border border-stone-900 rounded-sm">
            {scenes.map((s, idx) => (
              <li key={s.scene_id} data-onboarding={idx === 0 ? "scene-card" : undefined} className="flex items-center gap-3 p-4 hover:bg-stone-900/60 transition-colors">
                <Link data-testid={`scene-open-${s.scene_id}`} to={`/scenes/${s.scene_id}`} className="flex-1 min-w-0">
                  <div className="serif text-xl text-stone-100 truncate">{s.title}</div>
                  <div className="mono text-[11px] text-stone-500 tracking-widest uppercase mt-1">
                    {new Date(s.created_at).toLocaleString(lang === "pl" ? "pl-PL" : "en-US")} · {s.analysis ? t("dash.scene_analyzed") : t("dash.scene_pending")}
                  </div>
                </Link>
                <button data-testid={`scene-delete-${s.scene_id}`} onClick={() => del(s.scene_id)} className="ghost-btn !p-2 !border-stone-800 hover:!border-rose-500/60 hover:text-rose-400">
                  <Trash2 size={14}/>
                </button>
                <Link to={`/scenes/${s.scene_id}`} className="text-stone-500 hover:text-amber-500">
                  <ChevronRight />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>

    <WhatNextPaywall
      open={scenePaywallOpen}
      kind="scene"
      lang={lang}
      onClose={() => setScenePaywallOpen(false)}
      onSeeAllPlans={() => { setScenePaywallOpen(false); navigate("/pricing"); }}
    />
    </div>
  );
}
